import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { QueryClient } from '@tanstack/react-query';
import type { PersistQueryClientOptions, Persister } from '@tanstack/react-query-persist-client';
import { createStore, del, get, set } from 'idb-keyval';

import { BUILD_ID } from '@/lib/build-id';

/**
 * =============================================================================
 *  persistQueryClient → IndexedDB（ARCHITECTURE §10.2 規則 1）
 *
 *  規格逐字：「第二天早上 dashboard 在網路回來之前就從硬碟畫好，然後再驗證。
 *  這是『快』與『瞬間』的差別。」ROADMAP §5 驗收 #1 把它量化成
 *  **二次載入首次繪製 < 400ms**。
 *
 *  三個決定，每一個都對應一個具體的壞法：
 *
 *  1. **IndexedDB 而不是 localStorage。** 5 年日 K 的欄式 payload 一檔就有
 *     數百 KB，localStorage 的 5MB 上限會在第三檔股票時安靜地丟 QuotaExceeded，
 *     而且它是同步的——在主執行緒上序列化 300KB 就是一次掉幀。
 *
 *  2. **`buster` 綁 build hash**（見 lib/build-id.ts）。少了它，改過 payload
 *     形狀的新前端會去讀舊前端寫下的快取，然後少畫一條線而不報錯。
 *
 *  3. **只持久化成功的查詢，而且挑過 key。** 搜尋結果（symbol-search）
 *     與 /status 不進硬碟：前者是打字過程的殘骸，後者是維運畫面，
 *     一個 24 小時前的「全部綠燈」比沒有畫面更糟。
 * ========================================================================== */

export const PERSIST_MAX_AGE_MS = 24 * 60 * 60 * 1000; // §10.2：24h

const DB_NAME = 'stock-query-cache';
const STORE_NAME = 'tanstack-query';

/**
 * fixture 模式（`npm run dev:fixtures`）用**不同的 key**。
 *
 * 少了這一行，開發時 fixture 的假 K 線會被寫進硬碟，然後某天有人切回真的 API、
 * 打開同一檔股票、在網路回來之前看到的是假資料——而且它看起來完全正常。
 * 同一個 IndexedDB 裡放兩份，切換模式就是換一把鑰匙，不需要手動清快取。
 */
const CACHE_KEY =
  import.meta.env['VITE_API_FIXTURES'] === '1' ? 'react-query-fixture' : 'react-query';

/**
 * idb-keyval 的 store 是 lazy 的：在不支援 IndexedDB 的環境（Safari 無痕、
 * 某些嵌入式 WebView）裡 `createStore` 本身就會丟。整個持久層是**選配**——
 * 沒有它應用程式仍然完整，只是第二天早上會慢一點。所以這裡吞掉錯誤，
 * 而不是讓一個快取層把首頁打掛。
 */
function createIdbStore(): ReturnType<typeof createStore> | null {
  try {
    return createStore(DB_NAME, STORE_NAME);
  } catch {
    return null;
  }
}

export function createQueryPersister(): Persister | null {
  const store = createIdbStore();
  if (store === null) return null;

  return createAsyncStoragePersister({
    key: CACHE_KEY,
    // 節流：抓完一檔股票會連續寫三次（security / kpis / ohlcv），
    // 每次都序列化整個快取是白費的。1 秒是「切換股票的節奏」與「離開分頁前寫得完」的折衷。
    throttleTime: 1_000,
    storage: {
      getItem: async (key) => (await get<string>(key, store)) ?? null,
      setItem: async (key, value) => {
        await set(key, value, store);
      },
      removeItem: async (key) => {
        await del(key, store);
      },
    },
  });
}

let appPersister: Persister | null | undefined;

/**
 * 整個 app 共用的**那一個** persister：`__root.tsx` 用它掛 PersistQueryClientProvider，
 * 登出時 `clearUserCache` 用它把硬碟上的快取刪掉。兩邊拿到的必須是同一個物件——
 * 各建一個的話，IndexedDB 連線會開兩條，節流中的寫入也各自排隊。
 */
export function appQueryPersister(): Persister | null {
  if (appPersister === undefined) appPersister = createQueryPersister();
  return appPersister;
}

/**
 * 登出（或換人）時呼叫：記憶體裡的快取整個清掉，硬碟上那一份也刪掉。
 *
 * `'user'` 命名空間的 key 本來就不進硬碟（見 {@link shouldPersistQuery}），但**記憶體裡**
 * 還有上一個人的自選股與「我是誰」；而其他依工作階段而異的回應（模型輸出的存取閘）
 * 也不該留給下一個坐到這台電腦前的人。
 */
export async function clearUserCache(
  queryClient: QueryClient,
  persister: Persister | null,
): Promise<void> {
  queryClient.clear();
  await persister?.removeClient();
}

/** 哪些 query key 值得進硬碟。第一段永遠是 'stock'（見 lib/api/query-keys.ts）。 */
export function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  const kind = queryKey[1];
  // 綁使用者的資料（`qk.me` / 自選股，第二段是 'user'）不進硬碟：登出、換人之後
  // 上一個人的資料不能還躺在這台電腦的 IndexedDB 裡。
  if (kind === 'user') return false;
  // 搜尋殘骸與維運畫面不進硬碟；其餘（registry / indicators / security / kpis /
  // ohlcv / series）全部值得，因為它們就是盤前第一眼要看到的東西。
  if (kind === 'symbol-search' || kind === 'status') return false;
  // 模型輸出的回應依工作階段而異（MODEL_OUTPUT_ACCESS=allowlist 時只有名單內的人
  // 拿得到分數卡）。寫進 IndexedDB 就會在登出之後、換人之後還留在這台電腦上。
  const sub = queryKey[2];
  return (
    sub !== 'prediction' &&
    sub !== 'model-health'
  );
}

export function buildPersistOptions(
  persister: Persister,
): Omit<PersistQueryClientOptions, 'queryClient'> {
  return {
    persister,
    maxAge: PERSIST_MAX_AGE_MS,
    buster: BUILD_ID,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) =>
        query.state.status === 'success' && shouldPersistQuery(query.queryKey),
    },
  };
}
